export const getDocumentation = () => `
# EML: Entity Modeling Language

EML is a Domain-Specific Language (DSL) designed for modeling **Cyber-Physical Systems (CPS)**. It provides a structured and human-readable way to define entities (**Producers**, **Consumers**, **Hybrids**), their communication interfaces (MQTT, AMQP, Redis, REST), and their hierarchical relationships.

Built with [textX](https://github.com/textX/textX), EML enables rapid prototyping and formal validation of system architectures before implementation.

---

## 🚀 Key Features

- **Entity-Centric Modeling**: Define **Producer**, **Consumer**, **Hybrid**, and **Aggregation** entities with rich metadata.
- **Strict Aggregations**: Dedicated \`Aggregation\` entity type for cross-entity data aggregation using mathematical and logical expressions.
- **Hierarchical Structure**: Support for **Compartments** (nested entities) to model complex systems like robotic arms or vehicles.
- **Inheritance**: Reuse definitions using the \`extends\` keyword for modular design.
- **Multi-Protocol Support**: First-class support for **MQTT**, **AMQP**, **Redis**, and **REST** endpoints.
- **Strong Typing**: Built-in primitive types and support for custom \`DataType\` and \`Enum\` definitions.
- **Code Generation**: Generate Python code with built-in communication and state management logic.
- **JSON Interoperability**: Export models to JSON for integration with other tools and import them back to EML.
- **Validation CLI**: Built-in tool to validate models against the formal grammar and semantic rules.

---

## 🛠 Installation

### Prerequisites
- Python 3.9+
- Docker & Docker Compose (optional, for API deployment)
- A virtual environment (recommended)

### Setup
1. Clone the repository:
   \`\`\`bash
   git clone https://github.com/klpanagi/eml.git
   cd eml
   \`\`\`

2. Create and activate a virtual environment:
   \`\`\`bash
   python -m venv venv
   source venv/bin/activate
   \`\`\`

3. Install the package and dependencies:
   \`\`\`bash
   pip install -r requirements.txt
   pip install -e .
   \`\`\`

---

## 📖 Usage

### Validating Models
Use the \`eml\` CLI to validate your \`.eml\` files:

\`\`\`bash
eml validate examples/energy_monitoring.eml
\`\`\`

### Generating Python Code
Generate Python entity classes from your model:

\`\`\`bash
eml generate examples/energy_monitoring.eml -o generated_entities.py
\`\`\`

### JSON Integration
Export a model to JSON:
\`\`\`bash
eml export-json examples/energy_monitoring.eml -o model.json
\`\`\`

Import a JSON model back to EML:
\`\`\`bash
eml import-json model.json -o restored_model.eml
\`\`\`

---

## 🚀 Deployment (API Server)

EML comes with a FastAPI-based server that provides validation and transformation endpoints.

### Using Docker Compose
The easiest way to run the EML server is using Docker Compose:

\`\`\`bash
make up
\`\`\`

The API will be available at \`http://localhost:8000\`. You can access the interactive API documentation at \`http://localhost:8000/docs\`.

### Using the Makefile
- \`make build\`: Build the Docker image.
- \`make run\`: Run the server in a container.
- \`make stop\`: Stop the running container.
- \`make clean\`: Remove build artifacts.

---

## 📝 Grammar Overview

EML models follow a specific declaration order:
1. **Imports**: External grammar or model imports.
2. **DataTypes**: Custom data structures and enumerations.
3. **Brokers**: Message broker definitions (MQTT, AMQP, Redis).
4. **Endpoints**: REST API endpoint definitions.
5. **Entities**: The core system components.

### Example Syntax
\`\`\`eml
// Define a Broker
Broker::MQTT MyBroker @ localhost : 1883
    ssl: false
end

// Define a Producer (e.g., a Sensor)
Entity::Producer TempSensor @ MyBroker::"sensors/temp"
    description: "Living room temperature"
    mode: real
    updateRate: 5.0
    attributes:
        - value: float
end

// Define an Aggregation (Virtual Entity)
Entity::Aggregation HomeMonitor @ MyBroker::"home/monitor"
    description: "Aggregated home data"
    mode: virtual
    attributes:
        // Cross-entity reference and arithmetic expression
        - avgTemp: float = TempSensor.value / 1.0
        - isHot: bool = TempSensor.value > 30.0
end
\`\`\`

### Real-World Example: Smart Home Dashboard
For a more comprehensive example, see \`examples/smart_home_dashboard.eml\`, which demonstrates:
- Multiple **Producers** (Temperature, Humidity, Power, Motion).
- A **Consumer** (HVAC System) for climate control.
- A **Hybrid** entity for complex device feedback.
- A **Dashboard Aggregation** that calculates average temperatures, total power usage, and security alerts in real-time.

---

## 📂 Project Structure

- \`src/eml/grammar/\`: Formal textX grammar definitions.
- \`examples/\`: Comprehensive example models demonstrating various features.
- \`tests/\`: Pytest suite for grammar and semantic validation.
- \`scripts/\`: Automation scripts for validation and development.

---

## 🧪 Testing

Run the full test suite using \`pytest\`:

\`\`\`bash
pytest tests
\`\`\`

---

## 📄 License

This project is licensed under the MIT License - see the \`LICENSE\` file for details.
`;
